/**
 * Hook que retorna a lista de participantes da sessão.
 *
 * Lê o último sessionSnapshot recebido. participantUpdate adiciona/remove
 * conforme alunos entram/saem. Filtro padrão: apenas players (master geralmente
 * não atribui a si mesmo equipe).
 */

import { useEffect, useState } from 'react'

import { useAdapter } from '../adapter'

export interface Participant {
  membershipId: string
  displayName?: string | null
  role: 'master' | 'player'
  /** true = WS aberto agora; false = membership pré-populada sem conexão.
   *  Vem do snapshot; eventos joined implicam connected. Ausente em payloads
   *  antigos — trate undefined como conectado. */
  connected?: boolean
}

export function useParticipants(filter: 'all' | 'players' = 'players'): Participant[] {
  const adapter = useAdapter()
  const [participants, setParticipants] = useState<Participant[]>([])

  useEffect(() => {
    const apply = (list: Participant[]) => {
      if (filter === 'players') {
        setParticipants(list.filter((p) => p.role === 'player'))
      } else {
        setParticipants(list)
      }
    }

    const offSnap = adapter.on(
      'sessionSnapshot',
      (snap: { participants?: Participant[] }) => {
        apply(snap.participants ?? [])
      },
    )
    const offUpdate = adapter.on(
      'participantUpdate',
      // joined traz participant; left traz só membershipId no topo (ver
      // websocket.py — broadcast de saída não repete os dados do participante).
      (msg: { action: 'joined' | 'left'; participant?: Participant; membershipId?: string }) => {
        const membershipId = msg.participant?.membershipId ?? msg.membershipId
        if (!membershipId) return
        setParticipants((cur) => {
          const filtered = cur.filter((p) => p.membershipId !== membershipId)
          if (msg.action === 'joined' && msg.participant) {
            const next = [...filtered, { ...msg.participant, connected: true }]
            return filter === 'players' ? next.filter((p) => p.role === 'player') : next
          }
          return filtered
        })
      },
    )
    return () => {
      offSnap()
      offUpdate()
    }
  }, [adapter, filter])

  return participants
}
